import { inject, Injectable } from "@angular/core";
import {
  BOIType, cloudFunctionNames,
  IEntity,
  IEntityMap,
  IEntityTotalProgress,
  IHttpsCallableResponse,
  IOwnerRelationship,
  IShareTypeReference,
  isProEntity,
  OwnerType,
  UserType,
  UUID
} from "../interfaces";
import {
  IEntityCf,
  IGetTotalEntityProgressReq,
  IGetTotalEntityProgressRes,
  IMirrorDeleteEntityFromUserReq,
  IMirrorEntityFromUserReq,
  IMirrorEntityProgressFromUserReq,
  isFileEntityCf,
  isHandedOffEntityCf,
  isProEntityCf,
} from "@cloud-functions/interfaces";
import { Functions } from "@angular/fire/functions";
import { httpsCallable } from "firebase/functions";
import {
  arrayRemove,
  arrayUnion,
  collection,
  CollectionReference,
  deleteDoc,
  deleteField,
  doc,
  DocumentReference,
  getDoc,
  setDoc,
  updateDoc
} from "firebase/firestore";
import { convertDatesToTimestamps, convertTimestampsToDates, handleLoadingState } from "../utils/helper-functions";
import { OwnerService } from "src/app/state/owner-state/owner.service";
import { Firestore } from "@angular/fire/firestore";
import { GroupService } from "src/app/state/secure-pro-state/group.service";
import { EntityDetailsService } from "src/app/state/entity-details-state/entity-details.service";
import { FeatureFlagsService } from "./feature-flags.service";
import { BoirFilingsService } from "./boir-filings.service";
import { debounceTime, distinctUntilChanged, filter, map, Observable, switchMap, withLatestFrom } from "rxjs";
import { exhaustiveCheck } from "@cloud-functions/utils";
import { docData } from "rxfire/firestore";
import { updateDocWithFlatten } from "../utils/common-db-operations";
import { UpdateData } from "@firebase/firestore";
import { DocumentManagementService } from "src/app/state/document-management/document-management.service";

export type IEntityOperation = keyof Pick<IEntityService,
  | "createEntity"
  | "updateEntity"
  | "deleteEntity"
  | "getTotalEntityProgress"
>;

export interface IEntityService {
  getEntity(entityId: UUID, orgId?: UUID): Promise<IEntity>;
  getProEntity(orgId: UUID, entityId: UUID): Promise<IEntity>;
  getFileEntity(entityId: UUID): Promise<IEntity>;

  createEntity(entity: IEntity): Promise<IEntity>;
  updateEntity(entity: IEntity): Promise<IEntity>;
  deleteEntity(entityId: UUID, orgId?: UUID): Promise<IEntity>;

  getTotalEntityProgress(entityId: UUID, orgId: UUID | null): Promise<IEntityTotalProgress>;

  mirrorEntityFromUser(entityId: UUID, fromUserType: UserType, orgId?: UUID): Promise<void>;
  mirrorEntityProgressFromUser(entityId: UUID, orgId?: UUID): Promise<void>;
  mirrorDeleteEntityFromUser(entityId: UUID, orgId?: UUID): Promise<void>
}

@Injectable()
export abstract class EntityService implements IEntityService {
  protected firestore: Firestore = inject(Firestore);
  protected functions: Functions = inject(Functions);
  protected ownerService: OwnerService = inject(OwnerService);
  protected groupService: GroupService = inject(GroupService);
  protected entityDetailsService: EntityDetailsService = inject(EntityDetailsService);
  protected featureFlags: FeatureFlagsService = inject(FeatureFlagsService);
  protected boirFilingsService: BoirFilingsService = inject(BoirFilingsService);
  protected documentManagementService: DocumentManagementService = inject(DocumentManagementService);

  protected userType: UserType;

  private readonly restrictedToUpdateFields = new Set<keyof IEntityCf>([
    "ownerRelationships",
    "bulkUploaded",
    "bulkUploadedDate",
    "groupId",
    "groupName",
    "progress",
  ]);

  constructor(userType: UserType) {
    this.userType = userType;
  }

  protected convertEntityToCf(entity: IEntity): IEntityCf {
    return convertDatesToTimestamps(entity) as IEntityCf;
  }

  protected convertEntityFromCf(entity: IEntityCf): IEntity {
    return convertTimestampsToDates(entity) as IEntity;
  }

  protected convertEntityArrayToMap(entities: IEntity[]): IEntityMap {
    return entities.reduce((map, entity) => {
      map[entity.id] = entity;
      return map;
    }, {} as IEntityMap);
  }

  protected getFileEntityRef(entityId: UUID): DocumentReference<IEntityCf> {
    return doc(
      this.firestore,
      `secureFileEntities/${entityId}`,
    ) as DocumentReference<IEntityCf>;
  }

  protected getProEntityRef(orgId: UUID, entityId: UUID): DocumentReference<IEntityCf> {
    return doc(
      this.firestore,
      `organization/${orgId}/entityList/${entityId}`,
    ) as DocumentReference<IEntityCf>;
  }

  public static isOwnerPresentInEntity(
    entity: IEntity,
    ownerId: UUID
  ): entity is IEntity & { ownerRelationships: Record<UUID, IOwnerRelationship> } {
    return (
      Object.prototype.hasOwnProperty.call(entity.ownerRelationships, ownerId) &&
      !!entity.ownerRelationships[ownerId]
    );
  }

  protected getProEntityCollectionDoc(orgId: UUID): CollectionReference<IEntityCf> {
    return collection(
      this.firestore,
      `organization/${orgId}/entityList`
    ) as CollectionReference<IEntityCf>;
  }

  protected getFileEntityCollectionDoc(): CollectionReference<IEntityCf> {
    return collection(
      this.firestore,
      "secureFileEntities"
    ) as CollectionReference<IEntityCf>;
  }

  public async getEntity(entityId: UUID, orgId?: UUID): Promise<IEntity> {
    let entityRef: DocumentReference<IEntityCf>;
    switch (this.userType) {
      case "secure-pro":
        if (!orgId) throw new Error("Organization ID required for getting pro entity");
        entityRef = this.getProEntityRef(orgId, entityId);
        break;
      case "secure-file":
        entityRef = this.getFileEntityRef(entityId);
        break;
      default:
        exhaustiveCheck(this.userType, "Invalid user type");
        throw new Error("Unreachable code");
    }
    const entitySnap = await getDoc(entityRef);

    if (!entitySnap.exists()) {
      throw new Error("Entity not found")
    }

    return this.convertEntityFromCf(entitySnap.data());
  }

  public async getProEntity(orgId: UUID, entityId: UUID): Promise<IEntity> {
    const entityRef = this.getProEntityRef(orgId, entityId);
    const entitySnap = await getDoc(entityRef);

    if (!entitySnap.exists()) {
      throw new Error("Entity not found")
    }

    return this.convertEntityFromCf(entitySnap.data());
  }

  public async getFileEntity(entityId: UUID): Promise<IEntity> {
    const entityRef = this.getFileEntityRef(entityId);
    const entitySnap = await getDoc(entityRef);

    if (!entitySnap.exists()) {
      throw new Error("Entity not found")
    }

    return this.convertEntityFromCf(entitySnap.data());
  }

  public async createEntity(entity: IEntity): Promise<IEntity> {
    const entityCf = this.convertEntityToCf(entity);
    switch (this.userType) {
      case "secure-pro":
        await this.createProEntity(entityCf);
        break;
      case "secure-file":
        await this.createFileEntity(entityCf);
        break;
      default:
        exhaustiveCheck(this.userType, "Invalid user type");
    }
    return entity;
  }

  private async createProEntity(entity: IEntityCf): Promise<void> {
    if (!isProEntityCf(entity)) {
      throw new Error("Invalid entity type, expected secure-pro entity");
    }
    const entityRef = this.getProEntityRef(entity.managingOrgId, entity.id);
    const entitySnap = await getDoc(entityRef);
    if (entitySnap.exists()) {
      throw new Error("Entity already exists");
    }
    return setDoc(entityRef, entity);
  }

  private async createFileEntity(entity: IEntityCf): Promise<void> {
    if (!isFileEntityCf(entity)) {
      throw new Error("Invalid entity type, expected secure-file entity");
    }
    const entityRef = this.getFileEntityRef(entity.id);
    const entitySnap = await getDoc(entityRef);
    if (entitySnap.exists()) {
      throw new Error("Entity already exists");
    }
    return setDoc(entityRef, entity);
  }

  public async updateEntity(entity: IEntity): Promise<IEntity> {
    const entityCf = this.convertEntityToCf(entity);
    switch (this.userType) {
      case "secure-pro":
        await this.updateProEntity(entityCf);
        break;
      case "secure-file":
        await this.updateFileEntity(entityCf);
        break;
      default:
        exhaustiveCheck(this.userType, "Invalid user type");
    }
    return entity;
  }

  private async updateProEntity(entity: IEntityCf): Promise<void> {
    if (!isProEntityCf(entity)) {
      throw new Error("Invalid entity type, expected secure-pro entity");
    }
    const entityRef = this.getProEntityRef(entity.managingOrgId, entity.id);
    return updateDoc(entityRef, this.removeRestrictedToUpdateFields(entity) as UpdateData<IEntityCf>);
  }

  private async updateFileEntity(entity: IEntityCf): Promise<void> {
    if (!isFileEntityCf(entity) && !isHandedOffEntityCf(entity)) {
      throw new Error("Invalid entity type, expected secure-file or handed-off entity");
    }
    const entityRef = this.getFileEntityRef(entity.id);
    return updateDoc(entityRef, this.removeRestrictedToUpdateFields(entity) as UpdateData<IEntityCf>);
  }

  private removeRestrictedToUpdateFields(entity: IEntityCf): Partial<IEntityCf> {
    const filteredEntity = Object.fromEntries(
      Object.entries(entity).filter(
        ([key]) => !this.restrictedToUpdateFields.has(key as keyof IEntityCf)
      )
    ) as Partial<IEntityCf>;

    return filteredEntity;
  }

  public deleteEntity(entityId: UUID, orgId?: UUID): Promise<IEntity> {
    switch (this.userType) {
      case "secure-pro":
        if (!orgId) throw new Error("Organization ID required for deleting pro entity");
        return this.deleteProEntity(entityId, orgId);
      case "secure-file":
        return this.deleteFileEntity(entityId);
      default:
        exhaustiveCheck(this.userType, "Invalid user type");
        throw new Error("Unreachable code");
    }
  }

  protected async deleteProEntity(entityId: UUID, orgId: UUID): Promise<IEntity> {
    const entityRef = this.getProEntityRef(orgId, entityId);
    const entityData = await this.getProEntity(orgId, entityId);
    if (!isProEntity(entityData)) {
      throw new Error("Invalid entity type: expected pro entity");
    }
    await deleteDoc(entityRef);
    return entityData;
  }

  protected async deleteFileEntity(entityId: UUID): Promise<IEntity> {
    const entityRef = this.getFileEntityRef(entityId);
    const entityData = await this.getFileEntity(entityId);
    await deleteDoc(entityRef);
    return entityData;
  }

  public async getTotalEntityProgress(
    entityId: UUID,
    orgId: UUID | null
  ): Promise<IEntityTotalProgress> {
    const getTotalEntityProgressCallable = httpsCallable<
      IGetTotalEntityProgressReq,
      IGetTotalEntityProgressRes
    >(this.functions, cloudFunctionNames.entity.getTotalEntityProgress);

    const response = await getTotalEntityProgressCallable({ entityId, orgId });

    if (!response.data.success) {
      throw new Error(response.data.message);
    }

    return response.data.entityTotalProgress;
  }

  public async mirrorEntityFromUser(entityId: UUID, orgId?: UUID): Promise<void> {
    const mirrorEntityFromUserCallable = httpsCallable<
      IMirrorEntityFromUserReq,
      IHttpsCallableResponse
    >(this.functions, cloudFunctionNames.entity.mirrorEntityFromUser);

    const mirrorEntityFromUserResponse = await mirrorEntityFromUserCallable({
      userType: this.userType,
      entityId,
      orgId,
    });

    if (!mirrorEntityFromUserResponse.data.success) {
      throw new Error(mirrorEntityFromUserResponse.data.message);
    }
  }

  public async mirrorEntityProgressFromUser(entityId: UUID, orgId?: UUID): Promise<void> {
    const mirrorEntityProgressFromUserCallable = httpsCallable<
      IMirrorEntityProgressFromUserReq,
      IHttpsCallableResponse
    >(this.functions, cloudFunctionNames.entity.mirrorEntityProgressFromUser);

    const mirrorEntityProgressFromUserResponse = await mirrorEntityProgressFromUserCallable({
      userType: this.userType,
      entityId,
      orgId,
    });

    if (!mirrorEntityProgressFromUserResponse.data.success) {
      throw new Error(mirrorEntityProgressFromUserResponse.data.message);
    }
  }

  public async mirrorDeleteEntityFromUser(entityId: UUID, orgId?: UUID): Promise<void> {
    const mirrorDeleteEntityFromUserCallable = httpsCallable<
      IMirrorDeleteEntityFromUserReq,
      IHttpsCallableResponse
    >(this.functions, cloudFunctionNames.entity.mirrorDeleteEntityFromUser);

    const mirrorDeleteEntityFromUserResponse = await mirrorDeleteEntityFromUserCallable({
      userType: this.userType,
      entityId,
      orgId,
    });

    if (!mirrorDeleteEntityFromUserResponse.data.success) {
      throw new Error(mirrorDeleteEntityFromUserResponse.data.message);
    }
  }

  protected async addOwnerBoiType(
    entityDoc: DocumentReference<IEntityCf>,
    ownerId: UUID,
    boiType: BOIType
  ): Promise<void> {
    return updateDoc(entityDoc, {
      [`ownerRelationships.${ownerId}.boiType`]: arrayUnion(boiType)
    });
  }

  protected async createOwnerRelationship(
    entityDoc: DocumentReference<IEntityCf>,
    ownerId: UUID,
    boiType: BOIType
  ): Promise<void> {
    const newRelationship: IOwnerRelationship = {
      id: ownerId,
      capTableDetails: {},
      managementRole: "",
      businessTitle: "",
      boiType: [boiType],
      ownerType: [OwnerType.OWNER],
    };

    return updateDocWithFlatten(entityDoc, {
      [`ownerRelationships.${ownerId}`]: newRelationship
    });
  }

  protected async removeOwnerBoiType(
    entityDoc: DocumentReference<IEntityCf>,
    ownerId: UUID,
    boiType: BOIType
  ): Promise<void> {
    return updateDoc(entityDoc, {
      [`ownerRelationships.${ownerId}.boiType`]: arrayRemove(boiType)
    });
  }

  protected async removeOwnerRelationship(
    entityDoc: DocumentReference<IEntityCf>,
    ownerId: UUID
  ): Promise<void> {
    return updateDoc(entityDoc, {
      [`ownerRelationships.${ownerId}`]: deleteField()
    });
  }

  public static isEntityWithoutProgressChanged(prev: IEntity | null, curr: IEntity | null): boolean {
    if (prev === curr) return true;
    if (prev === null || curr === null) return false;

    const prevWithoutProgress = { ...prev } as Partial<IEntity>;
    const currWithoutProgress = { ...curr } as Partial<IEntity>;

    prevWithoutProgress.progress = undefined;
    currWithoutProgress.progress = undefined;

    return JSON.stringify(prevWithoutProgress) === JSON.stringify(currWithoutProgress);
  }

  protected getEntityFirestoreObservable(
    entityRef: DocumentReference<IEntityCf>,
    entityUpdateFilter$: Observable<boolean>,
  ): Observable<IEntity | null> {
    return docData(entityRef).pipe(
      withLatestFrom(entityUpdateFilter$.pipe(
        switchMap(handleLoadingState),
      )),
      filter(([/* entityCf */, isEntityUpdating]) => !isEntityUpdating),
      debounceTime(1000),
      map(([entityCf]) => entityCf ? this.convertEntityFromCf(entityCf) : null),
      distinctUntilChanged(EntityService.isEntityWithoutProgressChanged)
    )
  }

  protected async handleOwnershipClearing(
    entity: IEntity,
    shareTypeIdsToUpdate: UUID[],
    orgId?: UUID,
  ): Promise<void> {
    const recordsToClearOwnership = this.getRecordsByShareTypeIds(entity, shareTypeIdsToUpdate);

    if (recordsToClearOwnership.length > 0) {
      await this.clearOwnershipOfCapTableRecords(entity.id, recordsToClearOwnership, orgId);
    }
  }

  protected async handleShareTypesDeletion(
    entity: IEntity,
    shareTypeIdsToDelete: UUID[],
    orgId?: UUID,
  ): Promise<IShareTypeReference> {
    const recordsToDelete = this.getRecordsByShareTypeIds(entity, shareTypeIdsToDelete);

    if (recordsToDelete.length > 0) {
      await this.removeCapTableRecords(entity.id, recordsToDelete, orgId);
    }

    return this.getRemainingShareTypes(entity, shareTypeIdsToDelete);
  }

  private getRecordsByShareTypeIds(
    entity: IEntity,
    shareTypeIds: UUID[]
  ): { ownerId: UUID; shareTypeId: UUID }[] {
    const records: { ownerId: UUID; shareTypeId: UUID }[] = [];

    for (const [ownerId, relationship] of Object.entries(entity.ownerRelationships)) {
      const capTableDetails = relationship?.capTableDetails || {};

      for (const shareTypeId of Object.keys(capTableDetails)) {
        if (shareTypeIds.includes(shareTypeId)) {
          records.push({ ownerId, shareTypeId });
        }
      }
    }

    return records;
  }

  private getRemainingShareTypes(
    entity: IEntity,
    shareTypeIdsToDelete: UUID[]
  ): IShareTypeReference {
    return Object.fromEntries(
      Object.entries(entity.capTable?.shareTypes || {}).filter(
        ([shareTypeId]) => !shareTypeIdsToDelete.includes(shareTypeId as UUID)
      )
    );
  }

  public abstract clearOwnershipOfCapTableRecords(
    entityId: UUID,
    records: { ownerId: UUID; shareTypeId: UUID }[],
    orgId?: UUID,
  ): Promise<void>

  public abstract removeCapTableRecords(
    entityId: UUID,
    records: { ownerId: UUID; shareTypeId: UUID }[],
    orgId?: UUID,
  ): Promise<void>
}
